import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { fetchProducts } from "../lib/api";
import { Button } from "../components/ui/button";
import { useCart } from "../context/CartContext";
import { formatUAH } from "../lib/format";
import { toast } from "sonner";
import { Trash2, Minus, Plus } from "lucide-react";

// Element palette
const CRYSTAL_COLORS = [
  { id: "aqua", label: "Crystal Blue Shade", src: "/generated/crystals/crystal-aqua.png" },
  { id: "sapphire", label: "Sapphire", src: "/generated/crystals/crystal-sapphire.png" },
  { id: "rose", label: "Light Rose AB", src: "/generated/crystals/crystal-rose.png" },
  { id: "citrine", label: "Citrine", src: "/generated/crystals/crystal-citrine.png" },
  { id: "peridot", label: "Peridot", src: "/generated/crystals/crystal-peridot.png" },
];
const PEBBLE_COLORS = [
  { id: "green", label: "Зелений авантюрин", src: "/generated/crystals/stone-green.png" },
  { id: "blue", label: "Синій содаліт", src: "/generated/crystals/stone-blue.png" },
  { id: "purple", label: "Фіолетовий чароїт", src: "/generated/crystals/stone-purple.png" },
  { id: "brown", label: "Коричневий яшма", src: "/generated/crystals/stone-brown.png" },
  { id: "beige", label: "Бежевий агат", src: "/generated/crystals/stone-beige.png" },
];
const OTHER_ITEMS = [
  { id: "pearl", label: "Перлина", src: "/generated/crystals/other-pearl.png" },
  { id: "star-blue", label: "Синя морська зірка", src: "/generated/crystals/other-star-blue.png" },
  { id: "star-orange", label: "Помаранчева морська зірка", src: "/generated/crystals/other-star-orange.png" },
  { id: "shell-white", label: "Біла черепашка", src: "/generated/crystals/other-shell-white.png" },
  { id: "shell-orange", label: "Помаранчева черепашка", src: "/generated/crystals/other-shell-orange.png" },
];

const PALETTES = [
  { key: "crystal", title: "Кристали Swarovski", items: CRYSTAL_COLORS, render: (it) => ({ kind: "crystal", src: it.src, label: it.label, size: 30 }) },
  { key: "pebble", title: "Морські камінці", items: PEBBLE_COLORS, render: (it) => ({ kind: "stone", src: it.src, label: it.label, size: 46 }) },
  { key: "other", title: "Інше", items: OTHER_ITEMS, render: (it) => ({ kind: "other", src: it.src, label: it.label, size: 48 }) },
];

let nextId = 1;

export default function Customizer() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [product, setProduct] = useState(null);
  const [bubble, setBubble] = useState(true);
  const [elements, setElements] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [baseColor, setBaseColor] = useState(null);
  const canvasRef = useRef(null);
  const dragRef = useRef(null);
  const { addItem, setOpen: openCart } = useCart();

  useEffect(() => {
    fetchProducts().then((all) => {
      setProducts(all);
      if (slug) {
        const found = all.find((p) => p.slug === slug);
        // Block coming_soon models from being customized (incl. direct URL access)
        const ok = found && !found.coming_soon ? found : null;
        setProduct(ok);
        if (ok) {
          setBaseColor(ok.colors && ok.colors.length ? ok.colors[0] : null);
          // Preload every color image so switching the base swimsuit is instant
          const urls = ok.color_images ? Object.values(ok.color_images).flat() : ok.images;
          (urls || []).forEach((src) => { const im = new Image(); im.src = src; });
        }
      }
    });
  }, [slug]);

  useEffect(() => { const t = setTimeout(() => setBubble(false), 6000); return () => clearTimeout(t); }, []);

  const addToCanvas = (spec) => {
    const c = canvasRef.current;
    if (!c) return;
    const rect = c.getBoundingClientRect();
    const id = nextId++;
    setElements((prev) => [...prev, { id, scale: 1, ...spec, x: rect.width / 2 - 12 + (Math.random() * 60 - 30), y: rect.height / 2 - 12 + (Math.random() * 80 - 40) }]);
    setSelectedId(id);
  };

  const onPointerDown = (e, id) => {
    e.preventDefault();
    e.stopPropagation();
    setSelectedId(id);
    const c = canvasRef.current.getBoundingClientRect();
    const el = elements.find((x) => x.id === id);
    dragRef.current = { id, offX: e.clientX - c.left - el.x, offY: e.clientY - c.top - el.y };
  };
  const onPointerMove = (e) => {
    if (!dragRef.current) return;
    const c = canvasRef.current.getBoundingClientRect();
    const { id, offX, offY } = dragRef.current;
    const el = elements.find((x) => x.id === id);
    const sz = el ? (el.kind === "emoji" ? 28 : el.size) * (el.scale || 1) : 24;
    const x = Math.max(0, Math.min(c.width - sz, e.clientX - c.left - offX));
    const y = Math.max(0, Math.min(c.height - sz, e.clientY - c.top - offY));
    setElements((prev) => prev.map((el) => (el.id === id ? { ...el, x, y } : el)));
  };
  const onPointerUp = () => { dragRef.current = null; };

  const removeAll = () => { setElements([]); setSelectedId(null); };
  const removeOne = (id) => { setElements((p) => p.filter((e) => e.id !== id)); setSelectedId((s) => (s === id ? null : s)); };

  const MIN_SCALE = 0.25, MAX_SCALE = 2.5, STEP = 0.15;
  const scaleSelected = (delta) => {
    if (selectedId == null) return;
    setElements((prev) => prev.map((el) => {
      if (el.id !== selectedId) return el;
      const next = Math.min(MAX_SCALE, Math.max(MIN_SCALE, +(el.scale || 1) + delta));
      return { ...el, scale: +next.toFixed(2) };
    }));
  };
  const selectedEl = elements.find((e) => e.id === selectedId) || null;

  const designPrice = elements.length === 0 ? 0 : elements.length >= 5 ? 1500 : Math.min(elements.length * 350, 1500);

  const baseImages = (product && product.color_images && baseColor && product.color_images[baseColor]) || (product && product.images) || [];
  const baseImage = baseImages[0];

  const onSave = () => {
    if (!product) { toast.error("Спочатку оберіть модель"); return; }
    if (elements.length === 0) { toast.error("Додайте хоча б один елемент декору"); return; }
    addItem({
      product_id: product.id,
      product_name: `${product.name_short} (Custom Design)`,
      base_price: product.price,
      size: product.sizes[0],
      color: baseColor || (product.colors[0] || ""),
      quantity: 1,
      customizations: [{ option_id: "custom-design", label: `Авторський дизайн (${elements.length} елементів)`, extra_price: designPrice, custom_text: null }],
      image: baseImage,
    });
    toast.success("Ваш дизайн додано до кошика");
    openCart(true);
  };

  // Step 1 — pick model
  if (!product) {
    return (
      <>
        <div className="bg-silea-cream min-h-[80vh]" data-testid="customizer-pick">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-12 py-16">
          <div className="flex items-end gap-6 mb-12">
            <img
              src="/generated/berry-static.webp"
              alt="Беррі"
              className="w-40 h-40 rounded-full object-cover border-2 border-silea-yellow shadow-lg bg-silea-cream"
              style={{ objectPosition: "center 25%", transform: "scale(1.05)" }}
              data-testid="berry-static-large"
            />
            <div className="bg-white border border-stone-200 px-6 py-4 rounded-sm relative">
              <p className="font-serif-display text-xl text-silea-navy">Ciao! Я Беррі 🐾</p>
              <p className="text-sm text-silea-navy/70 mt-1">Оберіть модель — і створимо купальник вашої мрії разом.</p>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
            {products.map((p) =>
              p.coming_soon ? (
                <div
                  key={p.id}
                  data-testid={`pick-${p.slug}`}
                  aria-disabled="true"
                  className="group block bg-white border border-stone-200 cursor-default select-none"
                >
                  <div className="aspect-[3/4] overflow-hidden bg-silea-navy flex items-center justify-center">
                    <div className="text-center px-4">
                      <p className="font-serif-display italic text-3xl text-silea-yellow leading-none">Coming</p>
                      <p className="font-serif-display italic text-3xl text-white/90 leading-none mt-1">soon</p>
                    </div>
                  </div>
                  <div className="p-5 flex justify-between items-baseline">
                    <h3 className="font-serif-display text-2xl text-silea-navy/60">{p.name_short}</h3>
                    <p className="eyebrow text-silea-navy/40">Soon</p>
                  </div>
                </div>
              ) : (
                <Link key={p.id} to={`/customizer/${p.slug}`} data-testid={`pick-${p.slug}`} className="group block bg-white border border-stone-200 hover:border-silea-navy transition-colors">
                  <div className="aspect-[3/4] overflow-hidden bg-stone-100">
                    <img src={p.images[0]} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                  </div>
                  <div className="p-5 flex justify-between items-baseline">
                    <h3 className="font-serif-display text-2xl text-silea-navy">{p.name_short}</h3>
                    <p className="text-silea-navy">{formatUAH(p.price)}</p>
                  </div>
                </Link>
              )
            )}
          </div>
        </div>
        </div>
      </>
    );
  }

  return (
    <div className="bg-silea-cream min-h-screen" data-testid="customizer-page" onPointerMove={onPointerMove} onPointerUp={onPointerUp}>
      <div className="max-w-[1500px] mx-auto px-4 lg:px-10 py-8 grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* LEFT: Lea + Canvas */}
        <div className="lg:col-span-7">
          <div className="flex items-end gap-4 mb-4">
            <img src="/generated/berry.webp" alt="Беррі" loading="lazy" decoding="async" className="w-24 h-24 rounded-full object-cover border-2 border-silea-yellow shadow-lg flex-shrink-0 bg-silea-cream" style={{ objectPosition: "center 22%", transform: "scale(1.05)" }} />
            {bubble && (
              <div className="bg-white border border-stone-200 px-4 py-3 rounded-sm relative max-w-md">
                <p className="font-serif-display text-base text-silea-navy">Ciao, мене звати Беррі.</p>
                <p className="text-xs text-silea-navy/70">Я допоможу вам зробити купальник вашої мрії. Перетягуйте елементи на купальник →</p>
              </div>
            )}
          </div>

          <div
            ref={canvasRef}
            className="relative bg-white border border-stone-200 overflow-hidden select-none"
            style={{ aspectRatio: "4/5" }}
            data-testid="design-canvas"
            onPointerDown={() => setSelectedId(null)}
          >
            <img src={baseImage} alt={product.name} className="w-full h-full object-cover pointer-events-none" />
            {elements.map((el) => {
              const base = el.kind === "emoji" ? 28 : el.size;
              const sz = base * (el.scale || 1);
              const isSel = el.id === selectedId;
              return (
              <div
                key={el.id}
                onPointerDown={(e) => onPointerDown(e, el.id)}
                onDoubleClick={() => removeOne(el.id)}
                data-testid={`canvas-element-${el.id}`}
                className="absolute cursor-move touch-none"
                style={{
                  left: el.x, top: el.y,
                  width: sz,
                  height: sz,
                  borderRadius: el.kind === "dot" ? "50%" : 0,
                  background: el.kind === "dot" ? el.color : "transparent",
                  border: el.kind === "dot" ? `1px solid ${el.border}` : "none",
                  boxShadow: el.glow ? `0 0 8px ${el.color}, inset 0 0 4px rgba(255,255,255,0.6)` : "none",
                  outline: isSel ? "2px dashed rgba(212,175,55,0.95)" : "none",
                  outlineOffset: 3,
                  fontSize: el.kind === "emoji" ? 22 : 0,
                  lineHeight: 1,
                  display: "flex", alignItems: "center", justifyContent: "center",
                }}
                title="Подвійний клік — видалити"
              >
                {el.kind === "emoji" ? el.emoji : null}
                {el.src ? (
                  <img
                    src={el.src}
                    alt={el.label}
                    draggable={false}
                    className="w-full h-full object-contain pointer-events-none"
                    style={{ filter: "drop-shadow(0 1px 2px rgba(0,0,0,0.35))" }}
                  />
                ) : null}
              </div>
              );
            })}
            {elements.length === 0 && (
              <div className="absolute inset-x-0 bottom-4 text-center text-xs text-white drop-shadow">
                Додайте елементи з панелі праворуч →
              </div>
            )}
          </div>

          {/* Scale control for the selected element */}
          <div className="mt-3 bg-white border border-stone-200 px-4 py-3 flex items-center justify-between" data-testid="scale-control">
            {selectedEl ? (
              <>
                <span className="text-xs text-silea-navy/70">Розмір елемента</span>
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => scaleSelected(-STEP)}
                    disabled={(selectedEl.scale || 1) <= MIN_SCALE}
                    data-testid="scale-down"
                    aria-label="Зменшити"
                    className="w-8 h-8 rounded-full border border-stone-300 flex items-center justify-center text-silea-navy hover:bg-silea-navy hover:text-white transition-colors disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-silea-navy"
                  >
                    <Minus size={14} />
                  </button>
                  <span data-testid="scale-value" className="w-14 text-center font-serif-display text-lg text-silea-navy tabular-nums">
                    {Math.round((selectedEl.scale || 1) * 100)}%
                  </span>
                  <button
                    onClick={() => scaleSelected(STEP)}
                    disabled={(selectedEl.scale || 1) >= MAX_SCALE}
                    data-testid="scale-up"
                    aria-label="Збільшити"
                    className="w-8 h-8 rounded-full border border-stone-300 flex items-center justify-center text-silea-navy hover:bg-silea-navy hover:text-white transition-colors disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-silea-navy"
                  >
                    <Plus size={14} />
                  </button>
                </div>
              </>
            ) : (
              <span className="text-xs text-silea-navy/50" data-testid="scale-hint">Торкніться елемента на купальнику, щоб змінити його розмір</span>
            )}
          </div>

          <div className="mt-3 flex items-center justify-between text-xs text-silea-navy/60">
            <span>Перетягуйте елементи. Подвійний клік — видалити.</span>
            <button onClick={removeAll} data-testid="clear-canvas" className="silea-link inline-flex items-center gap-1 text-silea-navy">
              <Trash2 size={12} /> Очистити
            </button>
          </div>
        </div>

        {/* RIGHT: Palette */}
        <aside className="lg:col-span-5 space-y-5">
          <div className="bg-white border border-stone-200 p-5">
            <p className="eyebrow text-silea-navy/60">Модель</p>
            <div className="flex items-center justify-between mt-1">
              <h2 className="font-serif-display text-3xl text-silea-navy">{product.name_short}</h2>
              <Link to="/customizer" className="text-xs silea-link text-silea-navy" data-testid="change-model">змінити</Link>
            </div>
            {product.colors && product.colors.length > 1 && (
              <div className="mt-4" data-testid="base-color-picker">
                <p className="eyebrow text-silea-navy/60 mb-2">Колір купальника — <span className="text-silea-navy">{baseColor}</span></p>
                <div className="flex flex-wrap gap-2">
                  {product.colors.map((c) => (
                    <button
                      key={c}
                      onClick={() => setBaseColor(c)}
                      data-testid={`base-color-${c}`}
                      className={`px-3 py-2 text-xs border transition-colors ${baseColor === c ? "border-silea-navy bg-silea-navy text-white" : "border-stone-300 text-silea-navy hover:border-silea-navy"}`}
                    >
                      {c}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {PALETTES.map((pal) => (
            <div key={pal.key} className="bg-white border border-stone-200 p-5" data-testid={`palette-${pal.key}`}>
              <p className="eyebrow text-silea-navy mb-3">{pal.title}</p>
              <div className="flex flex-wrap gap-3">
                {pal.items.map((it) => {
                  const spec = pal.render(it);
                  return (
                    <button
                      key={it.id}
                      onClick={() => addToCanvas(spec)}
                      data-testid={`add-${pal.key}-${it.id}`}
                      title={spec.src ? spec.label : `Додати ${pal.title.toLowerCase()}`}
                      className="w-11 h-11 rounded-full border-2 border-stone-200 hover:border-silea-navy transition-colors flex items-center justify-center overflow-hidden"
                    >
                      {spec.src ? (
                        <img src={spec.src} alt={spec.label} className="w-9 h-9 object-contain" style={{ filter: "drop-shadow(0 1px 2px rgba(0,0,0,0.25))" }} />
                      ) : (
                        <span style={{ width: spec.size + 4, height: spec.size + 4, borderRadius: "50%", background: spec.color, border: `1px solid ${spec.border}`, boxShadow: spec.glow ? `0 0 6px ${spec.color}` : "none" }} />
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}

          <div className="bg-silea-navy text-white p-5">
            <div className="flex justify-between text-sm">
              <span className="text-white/70">Купальник {product.name_short}</span>
              <span>{formatUAH(product.price)}</span>
            </div>
            <div className="flex justify-between text-sm mt-2">
              <span className="text-white/70">Дизайн ({elements.length} елементів)</span>
              <span>{formatUAH(designPrice)}</span>
            </div>
            <div className="border-t border-white/20 mt-3 pt-3 flex justify-between items-baseline">
              <span className="eyebrow text-white/60">Разом</span>
              <span className="font-serif-display text-2xl">{formatUAH(product.price + designPrice)}</span>
            </div>
            <Button onClick={onSave} data-testid="save-design-btn" className="w-full mt-4 bg-silea-yellow hover:bg-white text-silea-navy rounded-none h-12 eyebrow">
              Зберегти і додати до кошика
            </Button>
            <p className="text-[11px] text-white/50 mt-2 text-center">Виготовлення кастомного дизайну — 7–10 робочих днів</p>
          </div>
        </aside>
      </div>
    </div>
  );
}
